import {
    codeBlockLookBack,
    findCompleteCodeBlock,
    findPartialCodeBlock,
} from '@llm-ui/code';
import { markdownLookBack } from '@llm-ui/markdown';
import { throttleBasic, useLLMOutput, type LLMOutputComponent } from '@llm-ui/react';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import ShikiHighlighter from 'react-shiki';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { Icon } from '../../../concepts/resources/Icon';
import { Button, Colors } from '../../../ui';
import { CopyToClipboard } from '../../common/CopyToClipboard';
import { Flexer } from '../../dashboard/elements/LayoutElements';

const processOutput = (output: string) => {
    const match = output.match(/```(\w+)[\s\S]*```/);
    if (match) {
        const language = match[1];
        const code = output.match(/```\w+([\s\S]*)```/)?.[1]?.trim() || '';
        return [language, code];
    }
    return ['', output];
};

const ShikiBlock: LLMOutputComponent = ({ blockMatch }) => {
    const [language, code] = processOutput(blockMatch.output);
    return <ShikiHighlighter language={language} theme="github-light">
        {code.trim()}
    </ShikiHighlighter>;
};

const Article = styled.div`
  /* Base typography */
  font-size: 1rem;
  line-height: 1.5;
  color: ${Colors.text};
  margin: 0 auto;

  /* Headings */
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin-bottom: 1em;
    line-height: 1.3;
    color: ${Colors.textDark};
    font-weight: 500;

    &:first-child {
      margin-top: 0;
    }
  }

  & h1 {
    font-size: 1.125em;
  }

  & h2 {
    font-size: 1.125em;
  }

  & h3 {
    font-size: 1.125em;
  }

  & h4 {
    font-size: 1.125em;
  }

  & h5 {
    font-size: 1.125em;
  }

  & h6 {
    font-size: 1.1em;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  /* Paragraphs and spacing */
  & p {
    margin-bottom: 1em;
  }

  /* Links */
  & a {
    color: ${Colors.primary};
    text-decoration: underline;
    text-decoration-thickness: 0.05em;
    text-underline-offset: 0.15em;
    transition: color 0.2s ease;
  }

  & a:hover {
    color: ${Colors.primaryLight};
  }

  & a:focus {
    outline: 2px solid ${Colors.primary};
    outline-offset: 2px;
  }

  /* Lists */
  & ul,
  & ol {
    margin-bottom: 1em;
    padding-left: 2rem;
  }

  & li {
    margin-bottom: 0.5em;
  }

  & li > ul,
  & li > ol {
    margin-top: 0.5rem;
    margin-bottom: 0;
  }

  /* List markers */
  & ul {
    list-style-type: disc;
  }

  & ul ul {
    list-style-type: circle;
  }

  & ul ul ul {
    list-style-type: square;
  }

  & ol {
    list-style-type: decimal;
  }

  & ol ol {
    list-style-type: lower-alpha;
  }

  & ol ol ol {
    list-style-type: lower-roman;
  }

  /* Images */
  & img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 2rem 0;
  }

  /* Figures */
  & figure {
    margin: 2rem 0;
    display: block;
  }

  & figcaption {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: ${Colors.textLight};
    text-align: center;
    font-style: italic;
  }

  /* Blockquotes */
  & blockquote {
    margin: 2rem 0;
    padding: 1rem 0 1rem 2rem;
    border-left: 4px solid ${Colors.border};
    font-style: italic;
    color: ${Colors.textLight};
  }

  & blockquote p:last-child {
    margin-bottom: 0;
  }

  & strong {
    font-weight: 500;
  }

  & em {
    font-style: italic;
    font-variation-settings: 'ital' 0.5;
  }

  /* Code */
  & code {
    font-family:
      "SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace;
    font-size: 0.9em;
    background: ${Colors.hover};
    padding: 0.2em 0.4em;
    border-radius: 3px;
  }

  & pre {
    background: ${Colors.hover};
    border-radius: 4px;
    padding: 1rem;
    overflow-x: auto;
    margin: 1.5rem 0;
  }

  & pre code {
    background: transparent;
    padding: 0;
    font-size: 0.85em;
    line-height: 1.6;
  }

  /* Horizontal rule */
  & hr {
    height: 2px;
    background-color: ${Colors.border};
    border: none;
    margin: 3rem 0;
  }

  /* Tables */
  & table {
    width: 100%;
    border-collapse: collapse;
    margin: 2rem 0;
    font-size: 0.95em;
  }

  & th,
  & td {
    padding: 0.75rem;
    border: 1px solid ${Colors.border};
  }

  & th {
    background-color: ${Colors.borderLight};
    font-weight: 600;
    text-align: left;
  }

  & tbody tr:nth-child(odd) {
    background-color: ${Colors.slate50};
  }

  /* Abbreviations */
  & abbr[title] {
    text-decoration: underline dotted;
    cursor: help;
  }

  /* Small text */
  & small {
    font-size: 0.85em;
  }

  /* Responsive tweaks */
  @media (max-width: 768px) {
    font-size: 1rem;

    & h1 {
      font-size: 2rem;
    }

    & h2 {
      font-size: 1.75rem;
    }

    & h3 {
      font-size: 1.5rem;
    }

    & h4 {
      font-size: 1.25rem;
    }

    & blockquote {
      padding-left: 1rem;
    }
  }
`;

const unwrap = (output: string) => {
    const match = output.match(/```\w*\n\r?([\s\S]*)\n```/);
    if (match) {
        return match[1];
    }
    return output;
};

// Customize this component with your own styling
const CodeSelectorBlock: LLMOutputComponent = ({ blockMatch }) => {
    // const [language] = processOutput(blockMatch.output);
    // if (language === 'html') return <HtmlBlock blockMatch={blockMatch} />;
    return <Flexer>
        <Flexer relative border={`1px solid ${Colors.border}`} borderRadius=".5" overflow="hidden">
            <ShikiBlock blockMatch={blockMatch} />
        </Flexer>
        <Flexer height=".5" />
        <CopyToClipboard value={unwrap(blockMatch.output)}>
            <Button size="sm" variant="common">
                <Icon size="1" src="deqo://icons/hero/clipboard-copy" /> Copy
            </Button>
        </CopyToClipboard>
    </Flexer>;
};

// Customize this component with your own styling
const MarkdownBlock: LLMOutputComponent = ({ blockMatch }) => {
    const markdown = blockMatch.output;
    return <Article>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
    </Article>;
};

const throttle = throttleBasic({
    readAheadChars: 10,
    targetBufferChars: 7,
    adjustPercentage: 0.35,
    frameLookBackMs: 10000,
    windowLookBackMs: 2000,
});

const Frame = styled.div`
    padding-top: .5rem;
`;

const Item = styled.div`
    margin-bottom: 1rem;
`;

export const StreamedMessageContent = React.memo<{ content: string, complete: boolean }>(({ content, complete }) => {

    const { blockMatches } = useLLMOutput({
        llmOutput: content,
        fallbackBlock: {
            component: MarkdownBlock, // from Step 1
            lookBack: markdownLookBack(),
        },
        blocks: [
            {
                component: CodeSelectorBlock, // from Step 2
                findCompleteMatch: findCompleteCodeBlock(),
                findPartialMatch: findPartialCodeBlock(),
                lookBack: codeBlockLookBack(),
            },
        ],
        isStreamFinished: complete,
        throttle,
    });

    return <Frame>
        {blockMatches.map((blockMatch, index) => {
            const Component = blockMatch.block.component;
            return <Item key={index}>
                <Component blockMatch={blockMatch} />
            </Item>;
        })}
    </Frame>;
});
